import { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function CulversFunding({ refreshKey, extraRefreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, [refreshKey, extraRefreshKey]);

  async function fetchData() {
    try {
      const res = await apiFetch('/api/culvers-funding');
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  function startEdit(field, currentValue) {
    setEditing(field);
    setEditValue(String(currentValue || 0));
  }

  async function saveEdit(field) {
    setSaving(true);
    const newVal = parseFloat(editValue) || 0;
    const payload = {
      available401k: data.available401k,
      bonus: data.bonus,
      settlement: data.settlement,
      personalLoan: data.personalLoan,
      [field]: newVal,
    };
    try {
      await apiFetch('/api/culvers-funding', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      await fetchData();
    } catch (err) { console.error(err); }
    finally {
      setSaving(false);
      setEditing(null);
    }
  }

  function handleKeyDown(e, field) {
    if (e.key === 'Enter') saveEdit(field);
    if (e.key === 'Escape') setEditing(null);
  }

  function fmt(val) {
    return parseFloat(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  if (loading || !data) {
    return <section className="card"><h2>Culver's Funding</h2><p className="loading">Loading...</p></section>;
  }

  const rows = [
    { label: 'HELOC Available', sublabel: data.helocAccountName, value: data.helocAvailable, detail: `${fmt(data.helocCreditLine)} line - ${fmt(Math.abs(data.helocBalance))} used`, color: '#3b82f6' },
    { label: 'Brokerage', sublabel: data.brokerageAccountName, value: data.brokerageBalance, color: '#22c55e' },
    { label: '401k Available', value: data.available401k, field: 'available401k', color: '#8b5cf6' },
    { label: 'Bonus', value: data.bonus, field: 'bonus', color: '#f59e0b' },
    { label: 'Settlement', value: data.settlement, field: 'settlement', color: '#06b6d4' },
    { label: 'Personal Loan', value: data.personalLoan, field: 'personalLoan', color: '#ec4899' },
  ];

  // For the bar visualization
  const maxVal = Math.max(...rows.map(r => Math.abs(r.value)), 1);

  return (
    <section className="card cf-section">
      <h2>Culver's Funding</h2>

      <div className="cf-total">
        <span className="cf-total-label">Total Available Funding</span>
        <span className="cf-total-value">{fmt(data.totalFunding)}</span>
      </div>

      <div className="cf-rows">
        {rows.map((row) => {
          const pct = maxVal > 0 ? (Math.abs(row.value) / maxVal) * 100 : 0;
          const isEditing = editing === row.field;

          return (
            <div key={row.label} className="cf-row">
              <div className="cf-row-header">
                <div className="cf-row-label-col">
                  <span className="cf-row-dot" style={{ background: row.color }} />
                  <span className="cf-row-label">{row.label}</span>
                  {row.sublabel && <span className="cf-row-sublabel">{row.sublabel}</span>}
                </div>
                <div className="cf-row-value-col">
                  {isEditing ? (
                    <div className="cf-edit-inline">
                      <span className="cf-edit-dollar">$</span>
                      <input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, row.field)}
                        onBlur={() => saveEdit(row.field)}
                        autoFocus
                        className="cf-edit-input"
                        disabled={saving}
                      />
                    </div>
                  ) : (
                    <span
                      className={`cf-row-value${row.field ? ' cf-editable' : ''}`}
                      onClick={() => row.field && startEdit(row.field, row.value)}
                      title={row.field ? 'Click to edit' : undefined}
                    >
                      {fmt(row.value)}
                    </span>
                  )}
                </div>
              </div>
              <div className="cf-row-bar-track">
                <div className="cf-row-bar-fill" style={{ width: `${pct}%`, background: row.color }} />
              </div>
              {row.detail && <span className="cf-row-detail">{row.detail}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default CulversFunding;
